import { FC, useCallback, useEffect, useState } from "react";

import { useBlocNext } from "@blac/react";
import { Button } from "@9amhealth/shared";
import type { ZonedDateTime } from "@internationalized/date";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Path from "src/constants/path";
import { createAppPath } from "src/lib/createAppPath";
import MealInsightsCubit from "src/state/MealInsightsCubit/MealInsightsCubit";
import MealsStatsCubit from "src/state/MealsStatsCubit/MealsStatsCubit";
import { fileState, tracker } from "src/state/state";
import translate from "src/lib/translate";
import useEscapeKey from "src/ui/hooks/useEscapeKey";
import AppDialog from "src/ui/components/AppDialog/AppDialog";
import Translate from "src/ui/components/Translate/Translate";

import Feedback from "./Feedback";
import MealInsightActionButton from "./MealInsightActionButton";
import MealInsightBloc from "./MealInsightBloc";
import MealInsightContent from "./MealInsightContent";
import { showMealSavedToast } from "./mealSavedToast";
import MealTakenAtEditor from "./MealTakenAtEditor";
import {
  mealTakenAtAsZoned,
  toOffsetDateTimeString
} from "./mealInsightHelpers";

const MealInsightOverview: FC = () => {
  const [
    { mealInsight, error, loading, imageError, uploadProgress, previewUrl },
    {
      generateMealInsight,
      clearMealInsight,
      discardMealInsight,
      rateMealInsight,
      updateMealTakenAt
    }
  ] = useBlocNext(MealInsightBloc);
  const [, { invalidate: invalidateMeals }] = useBlocNext(MealInsightsCubit);
  const [, { invalidate: invalidateMealStats }] = useBlocNext(MealsStatsCubit);
  const params = useParams();
  const { id } = params;
  const [searchParams] = useSearchParams();
  const fromMeals = searchParams.get("from") === "meals";
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [takenAtDialogOpen, setTakenAtDialogOpen] = useState(false);
  const navigate = useNavigate();
  const displayImageUrl = imageUrl || previewUrl;

  useEffect(() => {
    if (id) {
      clearMealInsight();
      if (id && id !== Path.tmp) {
        void generateMealInsight(id);
        if (!previewUrl) {
          const maxWidth = 500;
          const windowWidth = window.innerWidth;
          const density = window.devicePixelRatio;
          const width = Math.round(Math.min(windowWidth, maxWidth) * density);
          void fileState.loadPreview(id, width, width).then(setImageUrl);
        }
      }
    }
  }, [id, generateMealInsight, previewUrl, clearMealInsight]);

  useEffect(() => {
    if (mealInsight?.insights) {
      tracker.track("Meal insight information displayed");
    }
  }, [mealInsight?.insights]);

  // Replace rather than push: the temporary insight page has nothing to show
  // once the upload failed, so it must not stay reachable via the back button.
  useEffect(() => {
    if (imageError || error) {
      navigate(createAppPath(fromMeals ? Path.meals : Path.home), {
        replace: true
      });
    }
  }, [imageError, error, fromMeals, navigate]);

  useEffect(() => {
    if (displayImageUrl) {
      setImageLoaded(false);
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = displayImageUrl;
    }
  }, [displayImageUrl]);

  const navigateToHome = useCallback(() => {
    invalidateMeals();
    invalidateMealStats();
    navigate(createAppPath(Path.home));
  }, [navigate, invalidateMeals, invalidateMealStats]);

  const goToMealsAndReload = useCallback(() => {
    invalidateMeals();
    invalidateMealStats();
    navigate(createAppPath(Path.meals));
  }, [navigate, invalidateMeals, invalidateMealStats]);

  const exitToOrigin = useCallback(() => {
    if (fromMeals) {
      goToMealsAndReload();
    } else {
      navigateToHome();
    }
  }, [fromMeals, goToMealsAndReload, navigateToHome]);

  const handleSave = useCallback(() => {
    if (!fromMeals) {
      showMealSavedToast(navigate);
    }
    exitToOrigin();
  }, [navigate, fromMeals, exitToOrigin]);

  const handleDiscard = useCallback(async () => {
    const discarded = await discardMealInsight();
    if (discarded) {
      exitToOrigin();
    }
  }, [discardMealInsight, exitToOrigin]);

  const handleConfirmDiscard = useCallback(async () => {
    setConfirmDiscardOpen(false);
    await handleDiscard();
  }, [handleDiscard]);

  const handleClose = useCallback(() => {
    setConfirmDiscardOpen(true);
  }, []);

  const takenAt = mealInsight?.takenAt;

  const handleTakenAtSaved = useCallback(
    (picked: ZonedDateTime) => {
      if (!takenAt || picked.compare(mealTakenAtAsZoned(takenAt)) === 0) {
        return;
      }
      void updateMealTakenAt(toOffsetDateTimeString(picked));
    },
    [takenAt, updateMealTakenAt]
  );

  useEscapeKey(handleClose);

  const insights = mealInsight?.insights;
  const isLoading = loading || !mealInsight;
  const mealIdentified = insights?.mealIdentified !== false;

  return (
    <>
      <MealInsightContent
        imageUrl={displayImageUrl}
        imageLoaded={imageLoaded}
        loading={isLoading}
        mealIdentified={mealIdentified}
        mealName={insights?.mealName}
        takenAt={takenAt}
        onEditTakenAt={() => setTakenAtDialogOpen(true)}
        caloriesRange={insights?.caloriesRange}
        insightsHtml={insights?.insights}
        nutrients={{
          protein: insights?.protein,
          fiber: insights?.fiber,
          totalFats: insights?.totalFats,
          totalCarbohydrates: insights?.totalCarbohydrates
        }}
        uploadProgress={uploadProgress}
        showCloseButton
        closeContext={fromMeals ? "meals" : "home"}
        onClose={handleClose}
        feedbackSlot={
          mealInsight ? (
            <Feedback onRate={(rating) => void rateMealInsight(rating)} />
          ) : null
        }
        actionSlot={
          mealInsight ? (
            <>
              <MealInsightActionButton
                label={<Translate msg="mealInsights.discard" />}
                onClick={() => void handleDiscard()}
              />
              <Button
                theme="sunrise"
                grow
                hideArrow
                aria-label={translate("mealInsights.save")}
                onPress={handleSave}
              >
                <Translate msg="mealInsights.save" />
              </Button>
            </>
          ) : null
        }
      />
      {takenAtDialogOpen && takenAt && (
        <MealTakenAtEditor
          takenAt={takenAt}
          onClose={() => setTakenAtDialogOpen(false)}
          onSave={handleTakenAtSaved}
        />
      )}
      {confirmDiscardOpen && (
        <AppDialog
          confirmText="mealInsights.discard.confirm"
          cancelText="mealInsights.delete.no"
          confirmArrow={false}
          destructive
          hideClose
          onCancel={() => setConfirmDiscardOpen(false)}
          onConfirm={() => void handleConfirmDiscard()}
        >
          <h5>
            <Translate msg="mealInsights.discard.confirmTitle" />
          </h5>
          <p>
            <Translate msg="mealInsights.discard.confirmBody" />
          </p>
        </AppDialog>
      )}
    </>
  );
};

export default MealInsightOverview;
